import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Box,
  Toolbar,
  IconButton,
  Typography,
  Menu,
  Container,
  Button,
  MenuItem,
  Badge,
  Popover,
  List,
  ListItem,
  ListItemText,
  Divider,
  Tooltip,
  ListItemAvatar,
  Avatar,
  Snackbar,
  Alert,
  Dialog,
  Stack,
  Chip
} from '@mui/material';
import { useNavigate, createSearchParams } from 'react-router';
import { LogoutApi, getNotificationApi, updateNotification, clearNotificationApi, getAllStates, getInternalUserAddressLocations, getExternalUserAddress } from 'services';
import BackgroundLetterAvatars from '../avatar/letterAvatar';
import { useOidc } from '@axa-fr/react-oidc';
import LogoutIcon from '@mui/icons-material/Logout';
import DescriptionIcon from '@mui/icons-material/Description';
import LockResetIcon from '@mui/icons-material/LockReset';
import NotificationsIcon from '@mui/icons-material/Notifications';
import EventNoteIcon from '@mui/icons-material/EventNote';
import NotificationsOffIcon from '@mui/icons-material/NotificationsOff';
import SearchIcon from '@mui/icons-material/Search';
import * as signalR from '@microsoft/signalr';
import { getToken } from '../../services/utils';
import CheckoutPage from '../../modules/quote/module/BottleOrder/CheckoutPage';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import { useCart } from '../../CartContext';
import { clientIdsWithExternalPermission, getExternalClientPermissions } from '../../helpers/externalClientPermissions';
import CloseIcon from '@mui/icons-material/Close';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import { MobileHeader } from './MobileHeader';
import { DesktopHeader } from './DesktopHeader';

export const Header = () => {
  const [anchorElNav, setAnchorElNav] = useState<null | HTMLElement>(null);
  const [anchorElUser, setAnchorElUser] = useState<null | HTMLElement>(null);
  const [quoteMenuBar, setQuoteMenuBar] = useState<null | HTMLElement>(null);
  const [catalogueMenuBar, setCatalogueMenuBar] = useState<null | HTMLElement>(null);
  const [adminMenuBar, setAdminMenuBar] = useState<null | HTMLElement>(null);
  const [dashboardMenuAnchor, setDashboardMenuAnchor] = useState<null | HTMLElement>(null);
  const [orderMenuAnchor, setOrderMenuAnchor] = useState<null | HTMLElement>(null);
  const [standardQuote, setStandardQuote] = useState(false);
  const [isSyncData, setIsSyncData] = useState(false);
  const [rolePermission, setRolePermission] = useState(false);
  const [menuKey, setMenuKey] = useState(0);
  const [anchorElNotification, setAnchorElNotification] = useState<null | HTMLElement>(null);
  const [notificationList, setNotificationList] = useState<any[]>([]);
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [checkout, setCheckout] = useState(false);
  const [getAllState, setGetAllState] = useState([]);
  const [addressList, setAddressList] = useState<any[]>([]);

  const navigate = useNavigate();
  const { logout } = useOidc();
  const { cart, refreshCartItems, selectedClientId, quoteGroups } = useCart();

  const jwt: any = localStorage.getItem('token');
  const labUserCheck: any = jwt ? JSON.parse(atob(jwt.split('.')[1])) : null;

  const hasAddContainerPermission = labUserCheck?.Permission?.includes('AddContainerOrder');
  const bottleOrderClientIds = clientIdsWithExternalPermission(
    getExternalClientPermissions(labUserCheck?.ClientPermissions),
    'BottleOrders'
  );

  const countedQuoteGroups = new Set<string>();
  const totalCartUnits = cart.reduce((sum: number, item: any) => {
    if (labUserCheck?.UserType === 'External' && item?.quoteAnalysisRefID != null) {
      const groupId = item?.draftBottleGroupID ?? `${item?.draftBottleGroupName ?? ''}__${item?.draftBottleGroupSize ?? ''}`;
      const groupKey = `${item.quoteAnalysisRefID}__${groupId}`;
      if (countedQuoteGroups.has(groupKey)) return sum;
      countedQuoteGroups.add(groupKey);
      return sum + 1;
    }

    const units = Number(item?.noOfUnits ?? 0);
    // Items without a unit quantity (e.g. "no result" analysis references) still count as 1 cart entry
    return sum + (Number.isFinite(units) && units > 0 ? units : 1);
  }, 0) + quoteGroups.filter((group: any) =>
    !cart.some((item: any) =>
      String(item?.draftBottleGroupID ?? '') === String(group.id ?? '') ||
      `${item?.draftBottleGroupName ?? ''}`.trim() === group.name
    )
  ).length;

  const handleOpenNavMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElNav(event.currentTarget);
  };

  const handleCloseNavMenu = () => {
    setAnchorElNav(null);
  };

  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const handleDashboardMenu = (event: React.MouseEvent<HTMLElement>) => {
    setDashboardMenuAnchor(event.currentTarget);
  };

  const handleDashboardClose = () => {
    setDashboardMenuAnchor(null);
  };

  const handleOrderMenu = (e: React.MouseEvent<HTMLElement>) => {
    setOrderMenuAnchor(e.currentTarget);
  };

  const handleOrderClose = () => {
    setOrderMenuAnchor(null);
  };

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setQuoteMenuBar(event.currentTarget);
    setMenuKey((prevKey) => prevKey + 1);
  };

  const handleClose = () => {
    setQuoteMenuBar(null);
  };

  const handleCatalogueMenu = (event: React.MouseEvent<HTMLElement>) => {
    setCatalogueMenuBar(event.currentTarget);
  };

  const handleCatalogueClose = () => {
    setCatalogueMenuBar(null);
  };

  const handleAdminClick = (event: React.MouseEvent<HTMLElement>) => {
    setAdminMenuBar(event.currentTarget);
  };

  const handleAdminClose = () => {
    setAdminMenuBar(null);
  };

  const handleNotificationClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElNotification(event.currentTarget);
  };

  const handleNotificationClose = () => {
    setAnchorElNotification(null);
  };

  const linkTo = (page: string) => {
    setAnchorElUser(null);
    navigate(`/${page}`);
  };

  const handleFaq = () => {
    navigate(`/home/support`);
    handleClose();
  };

  const handleQuoteWithOutClient = () => {
    navigate({
      pathname: "/home/generateQuote",
      search: createSearchParams({
        client: 'new'
      }).toString()
    });
    handleClose();
  };

  const handleQuoteWithClient = () => {
    navigate({
      pathname: "/home/generateQuote",
      search: createSearchParams({
        client: 'exist'
      }).toString()
    });
    handleClose();
  };

  const handleViewQuote = () => {
    navigate(`/home/viewQuote`);
    handleClose();
  };

  const handleCatalogue = (name: string) => {
    if (name === 'price') {
      navigate(`/home/priceList`);
    } else {
      navigate(`/home/compareTest`);
    }
    handleCatalogueClose();
  };

  const updatePassword = () => {
    navigate(`/home/changePassword`);
    handleCloseUserMenu();
  };

  const handleLogout = () => {
    LogoutApi().then((res: any) => {
      if (res.status === 200) {
        if (labUserCheck?.UserType === 'Internal') {
          logout();
          sessionStorage.clear();
        }
        localStorage.clear();
        navigate('/');
      }
    }).catch((error: any) => {
      if (labUserCheck?.UserType === 'Internal') {
        logout();
        sessionStorage.clear();
      }
      localStorage.clear();
      navigate('/');
    });
  };

  const handleNotificationRedirect = (params: any) => {
    const obj = {
      "id": params?.id,
      "userID": params?.userID,
      "userType": params?.userType,
      "isNotified": true,
      "notifiedDate": new Date().toISOString().split('T')[0],
      "pushNotificationId": params?.pushNotificationId
    };
    updateNotification(obj).then((res: any) => {
      if (res.status === 200) {
        if (params?.resource !== "") {
          navigate(params?.resource);
        }
        handleNotificationClose();
        getNotification();
      }
    }).catch((error: any) => {
      setError(true);
      setErrorMessage(error.message);
    });
  };

  const handleClearAll = () => {
    clearNotificationApi().then((res: any) => {
      if (res.status === 200) {
        setNotificationList([]);
        handleNotificationClose();
      }
    }).catch((error: any) => {
      setError(true);
      setErrorMessage(error.message);
    });
  };

  const getNotification = () => {
    getNotificationApi().then((res: any) => {
      if (res.status === 200) {
        if (res.data.length > 0) {
          setNotificationList(res.data);
        } else {
          setNotificationList([]);
        }
      }
    }).catch((error: any) => {
      setError(true);
      setErrorMessage(error.message);
    });
  };

  const getUserAddressApi = () => {
    if (labUserCheck?.UserType === 'External') {
      if (selectedClientId && bottleOrderClientIds.has(String(selectedClientId))) {
        getExternalUserAddress(selectedClientId).then((res: any) => {
          if (res.status === 200) {
            setAddressList(res?.data);
          }
        }).catch((error: any) => {
          setError(true);
          setErrorMessage(error.message || 'Failed to fetch address list');
        });
      } else {
        setAddressList([]);
      }
    } else {
      if (selectedClientId) {
        getInternalUserAddressLocations(1, selectedClientId, 'checkout').then((res: any) => {
          if (res.status === 200) {
            setAddressList(res?.data?.despatchAddresses);
          }
        }).catch((error: any) => {
          setError(true);
          setErrorMessage(error.message || 'Failed to fetch address list');
        });
      }
    }
  };

  const getAllStateApi = () => {
    getAllStates().then((res: any) => {
      if (res.status === 200) {
        setGetAllState(res.data);
      }
    }).catch((error: any) => {
      setError(true);
      setErrorMessage(error.message);
    });
  };

  useEffect(() => {
    getNotification();
    if (labUserCheck?.UserType === 'External') {
      getUserAddressApi();
    }
  }, []);

  useEffect(() => {
    if (selectedClientId) {
      refreshCartItems(selectedClientId);
    }
  }, [selectedClientId, refreshCartItems]);

  useEffect(() => {
    const isProd =
      window.location.origin === "https://elvisconnect.eurofinsanz.com";

    const connect = new signalR.HubConnectionBuilder()
      .withUrl(`${process.env.REACT_APP_BASE_URL}/hub/notifications`, {
        accessTokenFactory: async () => {
          const token = localStorage.getItem("token");
          if (!token) {
            return "";
          }

          const newToken = await getToken();
          return newToken || token;
        },

        // Dev only
        ...(!isProd && {
          transport: signalR.HttpTransportType.WebSockets,
        }),
      })
      .withAutomaticReconnect(
        isProd
          ? undefined // Production
          : [10000, 10000, 10000] // Development
      )
      .configureLogging(signalR.LogLevel.Information)
      .build();

    connect
      .start()
      .then(() => {
        // console.log("SignalR connection established");
      })
      .catch((err) => {
        // console.error("Connection failed:", err);
      });

    connect.on("SendNotification", (data: any) => {
      // console.log("received data: ", data);
      getNotification();
    });

    return () => {
      connect
        .stop()
        .then(() => {
          // console.log("SignalR connection stopped");
        })
        .catch(() => { });
    };
  }, []);

  useEffect(() => {
    if (labUserCheck && labUserCheck?.Permission) {
      if (labUserCheck?.Permission.includes('AddUser') || labUserCheck?.Permission.includes('ManageClientUsers')) {
        setRolePermission(true);
      }
      if (labUserCheck?.Permission.includes('StandardQuotes') || labUserCheck?.Permission === 'StandardQuotes') {
        setStandardQuote(true);
      }
      if (labUserCheck?.Permission.includes('SyncData') || labUserCheck?.Permission === 'SyncData') {
        setIsSyncData(true);
      }
    }
  }, [labUserCheck]);

  useEffect(() => {
    getAllStateApi();
  }, []);

  // Navigation props for child components
  const navigationProps = {
    labUserCheck,
    selectedClientId,
    linkTo,
    standardQuote,
    rolePermission,
    isSyncData,
    navigate,
    dashboardMenuAnchor,
    quoteMenuBar,
    catalogueMenuBar,
    adminMenuBar,
    orderMenuAnchor,
    handleDashboardMenu,
    handleDashboardClose,
    handleOrderMenu,
    handleOrderClose,
    handleMenu,
    handleClose,
    handleCatalogueMenu,
    handleCatalogueClose,
    handleAdminClick,
    handleAdminClose,
    handleQuoteWithOutClient,
    handleQuoteWithClient,
    handleViewQuote,
    handleCatalogue,
    handleFaq,
    menuKey
  };

  const isNotificationOpen = Boolean(anchorElNotification);

  return (
    <>
      <AppBar
        position="sticky"
        sx={{
          background: '#003883',
          color: '#FFFFFF',
          height: '70px',
          boxShadow: '0px 2px 1px -1px rgb(0 0 0 / 20%), 0px 1px 1px 0px rgb(0 0 0 / 10%), 0px 1px 6px 0px rgb(0 0 0 / 16%)'
        }}
      >
        <Container maxWidth="xl" sx={{ maxWidth: '1700px !important' }}>
          <Toolbar disableGutters sx={{ minHeight: '70px !important', justifyContent: 'space-between' }}>

            <MobileHeader
              anchorElNav={anchorElNav}
              handleOpenNavMenu={handleOpenNavMenu}
              handleCloseNavMenu={handleCloseNavMenu}
              navigationProps={navigationProps}
            />

            <DesktopHeader
              navigationProps={navigationProps}
            />

            {/* Right side icons - Compact for mobile */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 } }}>
              {/* Support */}
              <Tooltip title="Support" arrow>
                <IconButton
                  onClick={handleFaq}
                  sx={{
                    color: '#FFFFFF',
                    padding: { xs: '6px', sm: '8px' },
                    height: { xs: '36px', sm: '40px' },
                    width: { xs: '36px', sm: '40px' },
                  }}
                >
                  <SearchIcon sx={{ fontSize: { xs: '20px', sm: '24px' } }} />
                </IconButton>
              </Tooltip>

              {/* Notifications */}
              <Tooltip title="Notifications" arrow>
                <IconButton
                  onClick={handleNotificationClick}
                  sx={{
                    color: '#ffffff',
                    padding: { xs: '6px', sm: '8px' },
                    height: { xs: '36px', sm: '40px' },
                    width: { xs: '36px', sm: '40px' },
                  }}
                >
                  <Badge badgeContent={notificationList.length} color="error">
                    <NotificationsIcon sx={{ fontSize: { xs: '20px', sm: '24px' } }} />
                  </Badge>
                </IconButton>
              </Tooltip>

              <Popover
                open={isNotificationOpen}
                anchorEl={anchorElNotification}
                onClose={handleNotificationClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                <Box sx={{ width: { xs: '300px', sm: '350px' }, maxHeight: '500px', overflowY: 'auto', padding: '16px' }}>
                  {notificationList.length > 0 ? (
                    <>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                          Unread Notifications
                        </Typography>
                        <Button
                          size="small"
                          variant="outlined"
                          sx={{ textTransform: 'capitalize', fontSize: '12px' }}
                          color="primary"
                          onClick={handleClearAll}
                        >
                          Clear All
                        </Button>
                      </Box>
                      <List>
                        {notificationList.map((notification) => (
                          <React.Fragment key={notification.id}>
                            <ListItem
                              alignItems="flex-start"
                              sx={{ backgroundColor: '#9297c521' }}
                              button
                              onClick={() => handleNotificationRedirect(notification)}
                            >
                              <ListItemAvatar>
                                <Avatar sx={{ bgcolor: '#003883' }}>
                                  <EventNoteIcon />
                                </Avatar>
                              </ListItemAvatar>
                              <ListItemText
                                primary={notification.title}
                                secondary={notification.message}
                                primaryTypographyProps={{ fontWeight: 'bold' }}
                              />
                            </ListItem>
                            <Divider sx={{ marginY: '8px' }} />
                          </React.Fragment>
                        ))}
                      </List>
                    </>
                  ) : (
                    <Box sx={{ textAlign: 'center', padding: '16px', color: '#757575' }}>
                      <NotificationsOffIcon sx={{ fontSize: '48px', marginBottom: '8px', color: '#757575' }} />
                      <Typography variant="body2">No notifications available</Typography>
                    </Box>
                  )}
                </Box>
              </Popover>

              {/* Shopping Cart - Only for External users, Internal handled in BottleOrderPage */}
              {(['External'].includes(labUserCheck?.UserType) ||
                (labUserCheck?.UserType === 'Internal' && hasAddContainerPermission)) && (
                  <Tooltip title="Cart" arrow>
                    <IconButton
                      sx={{
                        color: '#ffffff',
                        padding: { xs: '6px', sm: '8px' },
                        height: { xs: '36px', sm: '40px' },
                        width: { xs: '36px', sm: '40px' },
                      }}
                      onClick={() => setCheckout(true)}
                    >
                      <Badge badgeContent={totalCartUnits} color="error">
                        <ShoppingCartIcon sx={{ fontSize: { xs: '20px', sm: '24px' } }} />
                      </Badge>
                    </IconButton>
                  </Tooltip>)}

              {/* User Profile Section - Redesigned with Badge for Internal Users */}
              <Button
                onClick={handleOpenUserMenu}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: { xs: 0, md: 1 },
                  padding: { xs: '4px', sm: '6px', md: '6px 12px' },
                  borderRadius: '8px',
                  textTransform: 'none',
                  color: '#ffffff',
                  backgroundColor: { xs: 'transparent', md: 'rgba(255, 255, 255, 0.1)' },
                  border: { xs: 'none', md: '1px solid rgba(255, 255, 255, 0.2)' },
                  minWidth: 'auto',
                  '&:hover': {
                    backgroundColor: { xs: 'rgba(255, 255, 255, 0.1)', md: 'rgba(255, 255, 255, 0.15)' },
                  },
                  transition: 'all 0.2s ease-in-out',
                  ml: { xs: 0.5, sm: 1 }
                }}
              >
                {/* Avatar with Badge Indicator */}
                <Box sx={{ position: 'relative' }}>
                  <Box sx={{
                    '& .MuiAvatar-root': {
                      width: { xs: 32, sm: 36, md: 40 },
                      height: { xs: 32, sm: 36, md: 40 },
                      fontSize: { xs: '14px', sm: '16px', md: '18px' }
                    }
                  }}>
                    <BackgroundLetterAvatars userData={labUserCheck} />
                  </Box>
                  {/* Orange dot indicator for Internal Users */}
                  {labUserCheck?.UserType === 'Internal' && (
                    <Box
                      sx={{
                        position: 'absolute',
                        bottom: 0,
                        right: 0,
                        width: { xs: 10, sm: 12 },
                        height: { xs: 10, sm: 12 },
                        borderRadius: '50%',
                        backgroundColor: '#EE7D11',
                        border: '2px solid #003883',
                        animation: 'pulse 2s infinite',
                        '@keyframes pulse': {
                          '0%, 100%': {
                            opacity: 1,
                            transform: 'scale(1)'
                          },
                          '50%': {
                            opacity: 0.8,
                            transform: 'scale(1.1)'
                          }
                        }
                      }}
                    />
                  )}
                </Box>

                {/* Desktop view - Name and Badge */}
                <Stack spacing={0} alignItems="flex-start" sx={{ display: { xs: 'none', lg: 'flex' } }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      lineHeight: 1.2,
                      fontSize: '13px',
                      maxWidth: '120px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {labUserCheck?.given_name}
                  </Typography>
                  {labUserCheck?.UserType === 'Internal' && (
                    <Chip
                      label='Eurofins User'
                      size="small"
                      sx={{
                        height: '18px',
                        fontSize: '10px',
                        fontWeight: 500,
                        backgroundColor: '#EE7D11',
                        color: '#ffffff',
                        '& .MuiChip-label': {
                          padding: '0 6px'
                        }
                      }}
                    />
                  )}
                </Stack>
                <KeyboardArrowDownIcon sx={{ fontSize: '20px', ml: 0.5, display: { xs: 'none', lg: 'block' } }} />
              </Button>
            </Box>

            {/* Checkout Dialog */}
            <Dialog
              open={checkout}
              onClose={() => setCheckout(false)}
              maxWidth='lg'
              fullWidth
            >
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 2 }}>
                <IconButton onClick={() => {
                  setCheckout(false);
                }}>
                  <CloseIcon />
                </IconButton>
              </Box>
              <CheckoutPage
                cart={cart}
                onSubmit={() => setCheckout(false)}
                onClose={() => setCheckout(false)}
                getAllState={getAllState}
                addressList={addressList}
                getUserAddressApi={getUserAddressApi}
              />
            </Dialog>

            <Menu
              sx={{
                mt: '12px',
                '& .MuiPaper-root': {
                  minWidth: '240px',
                  borderRadius: '8px',
                  boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.15)'
                }
              }}
              anchorEl={anchorElUser}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              keepMounted
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              open={Boolean(anchorElUser)}
              onClose={handleCloseUserMenu}
            >
              {/* User Info Header */}
              <Box sx={{ px: 2, py: 1.5, backgroundColor: '#f5f5f5' }}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Box sx={{ position: 'relative' }}>
                    <BackgroundLetterAvatars userData={labUserCheck} />
                    {labUserCheck?.UserType === 'Internal' && (
                      <Box
                        sx={{
                          position: 'absolute',
                          bottom: 0,
                          right: 0,
                          width: 12,
                          height: 12,
                          borderRadius: '50%',
                          backgroundColor: '#EE7D11',
                          border: '2px solid #f5f5f5'
                        }}
                      />
                    )}
                  </Box>
                  <Stack spacing={0.5}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {labUserCheck?.given_name}
                    </Typography>
                    {labUserCheck?.UserType === 'Internal' && (
                      <Chip
                        label='Eurofins User'
                        size="small"
                        icon={
                          <Box
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              backgroundColor: '#ffffff',
                              animation: 'pulse 2s infinite',
                              '@keyframes pulse': {
                                '0%, 100%': { opacity: 1 },
                                '50%': { opacity: 0.5 }
                              }
                            }}
                          />
                        }
                        sx={{
                          height: '22px',
                          fontSize: '11px',
                          fontWeight: 500,
                          backgroundColor: '#EE7D11',
                          color: '#ffffff',
                          '& .MuiChip-icon': {
                            marginLeft: '8px',
                            marginRight: '-4px'
                          }
                        }}
                      />
                    )}
                  </Stack>
                </Stack>
              </Box>

              <Divider />

              {/* Menu Items */}
              {labUserCheck?.UserType === 'External' && (
                <>
                  <MenuItem
                    onClick={() => { navigate(`/home/settings`); handleCloseUserMenu(); }}
                    sx={{ py: 1.5, gap: 1.5 }}
                  >
                    <SettingsRoundedIcon sx={{ fontSize: '20px', color: '#666' }} />
                    <Typography variant="body2">Settings</Typography>
                  </MenuItem>
                  <MenuItem
                    onClick={updatePassword}
                    sx={{ py: 1.5, gap: 1.5 }}
                  >
                    <LockResetIcon sx={{ fontSize: '20px', color: '#666' }} />
                    <Typography variant="body2">Change Password</Typography>
                  </MenuItem>
                </>
              )}
              {labUserCheck?.UserType !== 'External' && (
                <>
                  <MenuItem
                    onClick={() => { navigate(`/home/manage-profiles`); handleCloseUserMenu(); }}
                    sx={{ py: 1.5, gap: 1.5 }}
                  >
                    <AssignmentIndIcon sx={{ fontSize: '20px', color: '#666' }} />
                    <Typography variant="body2">Manage Profile</Typography>
                  </MenuItem>
                </>
              )}

              <MenuItem
                onClick={() => { navigate(`/home/releaseNotes`); handleCloseUserMenu(); }}
                sx={{ py: 1.5, gap: 1.5 }}
              >
                <DescriptionIcon sx={{ fontSize: '20px', color: '#666' }} />
                <Typography variant="body2">Release Notes</Typography>
              </MenuItem>

              <Divider />

              <MenuItem
                onClick={handleLogout}
                sx={{
                  py: 1.5,
                  gap: 1.5,
                  color: '#d32f2f',
                  '&:hover': {
                    backgroundColor: '#ffebee'
                  }
                }}
              >
                <LogoutIcon sx={{ fontSize: '20px' }} />
                <Typography variant="body2" sx={{ fontWeight: 500 }}>Logout</Typography>
              </MenuItem>
            </Menu>

          </Toolbar>
        </Container>
      </AppBar>

      {/* Error Snackbar */}
      <Snackbar
        open={error}
        autoHideDuration={6000}
        onClose={() => setError(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setError(false)} severity="error" sx={{ width: '100%' }}>
          {errorMessage}
        </Alert>
      </Snackbar>
    </>
  );
};